import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  EnvioApiError,
  identificar,
  type Canal,
  type CanalDoCliente,
  type EmpresaDoGrupo,
  type FuncionarioResumo,
} from "@/lib/envio-colaborador-api";
import { Moldura, Titulo } from "./moldura";
import { Turnstile } from "./turnstile";
import { registrarFalhaEnvio } from "@/lib/sentry-envio";

const ERRO_REDE =
  "Não foi possível concluir agora. Tente de novo em instantes.";
const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 80 }, (_, i) => ANO_ATUAL - 14 - i);
const classeSelect =
  "h-13 w-full rounded-xl border border-slate-300 bg-white px-3 text-base";

function mascararCpf(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
}

/** Data montada por texto (AAAA-MM-DD): new Date() mudaria o dia por causa do fuso. */
function dataIso(
  dia: string,
  mes: string,
  ano: string,
): string | null | "invalida" {
  if (!dia || !mes || !ano) return null;
  const [d, m, a] = [Number(dia), Number(mes), Number(ano)];
  const dt = new Date(Date.UTC(a, m - 1, d));
  if (
    dt.getUTCFullYear() !== a ||
    dt.getUTCMonth() !== m - 1 ||
    dt.getUTCDate() !== d
  )
    return "invalida";
  return `${ano}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
}

type Achado = {
  token: string;
  funcionario: FuncionarioResumo;
  canal: CanalDoCliente;
};

export function PassoIdentificacao({
  slug,
  canal,
  aviso,
  onIdentificado,
}: {
  slug: string;
  canal: Canal;
  aviso: string | null;
  onIdentificado: (
    token: string,
    funcionario: FuncionarioResumo,
    canal: CanalDoCliente,
  ) => void;
}) {
  const [cpf, setCpf] = useState("");
  const [dia, setDia] = useState("");
  const [mes, setMes] = useState("");
  const [ano, setAno] = useState("");
  const [cf, setCf] = useState<string | null>(null);
  const [reiniciar, setReiniciar] = useState(0);
  const [erro, setErro] = useState<string | null>(aviso);
  const [enviando, setEnviando] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaDoGrupo[] | null>(null);
  const [achado, setAchado] = useState<Achado | null>(null);
  const [falhaTurnstile, setFalhaTurnstile] = useState(false);

  const digitos = cpf.replace(/\D/g, "");
  const bruta = dataIso(dia, mes, ano);
  const dataInvalida = bruta === "invalida";
  const nascimento = dataInvalida ? null : bruta;

  function limpar() {
    setErro(null);
    setAchado(null);
    setEmpresas(null);
  }

  async function buscar(empresa?: string) {
    if (!cf || !nascimento) return;
    setErro(null);
    setEnviando(true);
    const dados = {
      cpf: digitos,
      nascimento,
      turnstileToken: cf,
      ...(empresa ? { empresa } : {}),
    };
    try {
      const r = await identificar(slug, dados);
      if ("escolherEmpresa" in r) setEmpresas(r.escolherEmpresa);
      else {
        setEmpresas(null);
        setAchado(r);
      }
    } catch (e) {
      setEmpresas(null);
      // Erro da API já foi registrado no cliente; o resto é bug desta tela.
      if (!(e instanceof EnvioApiError)) registrarFalhaEnvio("identificar", e);
      setErro(e instanceof EnvioApiError ? e.message : ERRO_REDE);
    } finally {
      // O token do Turnstile vale uma vez: pede outro para a próxima chamada.
      setCf(null);
      setReiniciar((n) => n + 1);
      setEnviando(false);
    }
  }

  const classeBotao = "h-14 rounded-xl text-base";
  const acao = achado ? (
    <Button
      className={classeBotao}
      onClick={() =>
        onIdentificado(
          achado.token,
          achado.funcionario,
          achado.canal,
        )
      }
    >
      Sou eu, continuar
    </Button>
  ) : empresas ? null : (
    <Button
      className={classeBotao}
      disabled={digitos.length !== 11 || !nascimento || !cf || enviando}
      onClick={() => buscar()}
    >
      Continuar
    </Button>
  );

  return (
    <Moldura canal={canal} passo={1} acao={acao}>
      <Titulo className="text-[26px]">Seus dados</Titulo>
      <p className="mb-4 text-[15px] text-slate-600">
        Para achar seu cadastro na empresa.
      </p>

      <Label htmlFor="cpf">CPF</Label>
      <Input
        id="cpf"
        inputMode="numeric"
        autoComplete="off"
        className="h-13 rounded-xl text-base"
        value={cpf}
        onChange={(ev) => {
          setCpf(mascararCpf(ev.target.value));
          limpar();
        }}
      />

      <fieldset className="mt-4">
        <legend className="mb-1.5 text-sm font-medium">
          Data de nascimento
        </legend>
        <div className="grid grid-cols-[1fr_2fr_1.4fr] gap-2">
          <div>
            <Label htmlFor="dia" className="sr-only">
              Dia
            </Label>
            <select
              id="dia"
              className={classeSelect}
              value={dia}
              onChange={(ev) => {
                setDia(ev.target.value);
                limpar();
              }}
            >
              <option value="">Dia</option>
              {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="mes" className="sr-only">
              Mês
            </Label>
            <select
              id="mes"
              className={classeSelect}
              value={mes}
              onChange={(ev) => {
                setMes(ev.target.value);
                limpar();
              }}
            >
              <option value="">Mês</option>
              {MESES.map((m, i) => (
                <option key={m} value={String(i + 1)}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ano" className="sr-only">
              Ano
            </Label>
            <select
              id="ano"
              className={classeSelect}
              value={ano}
              onChange={(ev) => {
                setAno(ev.target.value);
                limpar();
              }}
            >
              <option value="">Ano</option>
              {ANOS.map((a) => (
                <option key={a} value={String(a)}>
                  {a}
                </option>
              ))}
            </select>
          </div>
        </div>
      </fieldset>


      <div className="mt-3">
        <Turnstile
          onToken={setCf}
          onErro={() => setFalhaTurnstile(true)}
          reiniciar={reiniciar}
        />
      </div>

      {empresas && !achado && (
        <div className="mt-4 flex flex-col gap-2">
          <p className="text-base font-bold">
            Para qual empresa é este atestado?
          </p>
          <p className="text-[13px] text-slate-600">
            Seu CPF está em mais de uma empresa. Escolha a empresa em que você
            faltou.
          </p>
          {empresas.map((e) => (
            <Button
              key={e.id}
              variant="outline"
              className="h-14 justify-start rounded-xl text-base"
              disabled={enviando || !cf}
              onClick={() => buscar(e.id)}
            >
              {e.nome}
            </Button>
          ))}
        </div>
      )}
      {achado && (
        <div className="mt-3 flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 p-3.5">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 font-bold text-green-800"
          >
            {achado.funcionario.nome.slice(0, 1)}
          </span>
          <span className="text-base font-bold">
            {achado.funcionario.nome}
          </span>
        </div>
      )}
      {dataInvalida && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          Data de nascimento inválida.
        </p>
      )}
      {falhaTurnstile && (
        <p
          role="alert"
          className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900"
        >
          Não foi possível validar seu acesso. Atualize a página e tente de
          novo.
        </p>
      )}
      {erro && (
        <p
          role="alert"
          className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900"
        >
          {erro}
        </p>
      )}
    </Moldura>
  );
}
